"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Loader2 from "lucide-react/dist/esm/icons/loader";
import Zap from "lucide-react/dist/esm/icons/zap";
import Download from "lucide-react/dist/esm/icons/download";
import Check from "lucide-react/dist/esm/icons/check";
import X from "lucide-react/dist/esm/icons/x";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import Heart from "lucide-react/dist/esm/icons/heart";
import InfinityIcon from "lucide-react/dist/esm/icons/infinity";
import Gift from "lucide-react/dist/esm/icons/gift";
import BadgePercent from "lucide-react/dist/esm/icons/badge-percent";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import Star from "lucide-react/dist/esm/icons/star";
import UserPlus from "lucide-react/dist/esm/icons/user-plus";
import { supabase } from "@/integrations/supabase/client";
import { useUsernameCheck } from "@/hooks/useUsernameCheck";
import PasswordInput from "@/components/auth/PasswordInput";
import PasswordStrength from "@/components/auth/PasswordStrength";
import { SignInForm } from "@/components/auth/SignInForm";
import { AuthFieldShell } from "@/components/auth/auth-form-ui";
import { FormAnticipationBar } from "@/components/ui/loading-anticipation";
import { Skeleton } from "@/components/ui/skeleton";
import { toUserFriendlyAuthError } from "@/lib/error-reporting";
import { reportIfRateLimited } from "@/lib/rate-limit-telemetry";
import { trackAuthSignupCompleted, trackAuthSignupStarted } from "@/lib/analytics/auth-tracking";
import { trackExpressSignupOpened, trackSignupVerificationPending } from "@/lib/analytics/events";
import type { SignupSurface } from "@/lib/analytics/events";
import { recordSignupSurface } from "@/lib/analytics/signup-surface";
import { TurnstileWidget, isTurnstileEnabled } from "@/components/auth/TurnstileWidget";
import { appendAttributionToUrl, getFirstTouchAttribution } from "@/lib/attribution";
import { stashPendingConsent } from "@/lib/pending-consent";
import { CheckYourEmailScreen } from "@/components/auth/CheckYourEmailScreen";
import { ResendVerification } from "@/components/auth/ResendVerification";
import { REFERRAL_GOAL } from "@/lib/referrals";
import { evaluatePasswordPolicy, passwordPolicyMessage, AUTH_PASSWORD_MIN_LENGTH } from "@/lib/password-policy";
import {
  savePendingIntent,
  clearPendingIntent,
  intentAssetId,
  intentToTrigger,
  type PendingIntent,
} from "@/lib/pending-intent";
import { cn } from "@/lib/utils";

export interface SignUpFlowContext {
  /** Title of the thing being acquired (asset name, seller name…). */
  title?: string;
  thumbnail?: string | null;
  /** Whether the asset is free — drives funnel segmentation. */
  isFree?: boolean;
  /** Seller of the asset being acquired, for funnel attribution. */
  sellerId?: string;
}

interface SignUpFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The action the visitor was trying to take — persisted so it auto-resumes
   *  after OAuth or the email-verification round-trip. */
  intent: PendingIntent;
  context?: SignUpFlowContext;
  /** Headline override for generic (non-intent) openers. */
  message?: string;
  onSuccess?: () => void;
  /** Analytics surface — "express_dialog" on asset pages, "auth_modal" when
   *  opened through the global auth-modal context. */
  surface?: SignupSurface;
}

type Step = "benefits" | "account" | "inbox" | "signin";
const FLOW: Step[] = ["benefits", "account", "inbox"];

interface BenefitItem {
  icon: LucideIcon;
  title: string;
  sub: string;
  highlight?: boolean;
}

/** Verb shown in headlines/buttons for each intent kind. */
function actionLabelFor(intent: PendingIntent): string {
  switch (intent.kind) {
    case "download": return "download";
    case "buy": return "purchase";
    case "cart": return "add to cart";
    case "wishlist": return "wishlist";
    case "checkout": return "checkout";
    case "subscribe": return "free trial";
    case "follow": return "follow";
    case "review": return "review";
    default: return "sign up";
  }
}

function submitLabelFor(intent: PendingIntent): string {
  switch (intent.kind) {
    case "download": return "Sign up & download";
    case "buy": return "Sign up & buy now";
    case "cart": return "Sign up & add to cart";
    case "wishlist": return "Sign up & save";
    case "checkout": return "Sign up & check out";
    case "subscribe": return "Sign up & start free trial";
    case "follow": return "Sign up & follow";
    case "review": return "Sign up & review";
    default: return "Create account";
  }
}

const DEFAULT_BENEFITS: BenefitItem[] = [
  { icon: Download, title: "Download free assets", sub: "Props, Verse, maps & more from real creators" },
  { icon: InfinityIcon, title: "7 free downloads a day", sub: "Go unlimited anytime with Buyer Plus" },
  { icon: Heart, title: "Wishlist & sale alerts", sub: "Save favorites, get pinged when they drop" },
  { icon: Gift, title: "Invite friends → free Buyer Plus", sub: `Bring ${REFERRAL_GOAL} friends, get a free week`, highlight: true },
];

function benefitsFor(intent: PendingIntent): BenefitItem[] {
  switch (intent.kind) {
    case "subscribe":
      // Trial framing is Buyer Plus specific; seller tiers get the generic list.
      if (intent.tierKey !== "buyer_plus") return DEFAULT_BENEFITS;
      return [
        { icon: InfinityIcon, title: "Unlimited downloads", sub: "No daily cap — grab everything you need", highlight: true },
        { icon: BadgePercent, title: "5% off every purchase", sub: "Buyer Plus discount applies at checkout automatically" },
        { icon: Heart, title: "Wishlist & sale alerts", sub: "Save favorites, get pinged when they drop" },
        { icon: ShieldCheck, title: "Cancel anytime", sub: "7 days free — no charge if you cancel before it ends" },
      ];
    case "checkout":
    case "buy":
    case "cart":
      return [
        { icon: ShieldCheck, title: "Secure checkout via Stripe", sub: "Cards, wallets — your details never touch our servers" },
        { icon: Download, title: "Yours forever", sub: "Every purchase lives in your library, re-download anytime" },
        { icon: Heart, title: "Wishlist & sale alerts", sub: "Save favorites, get pinged when they drop" },
        { icon: Gift, title: "Invite friends → free Buyer Plus", sub: `Bring ${REFERRAL_GOAL} friends, get a free week`, highlight: true },
      ];
    case "follow":
      return [
        { icon: Star, title: "Never miss a drop", sub: "Follow creators and see their new assets first" },
        { icon: Download, title: "Download free assets", sub: "Props, Verse, maps & more from real creators" },
        { icon: Heart, title: "Wishlist & sale alerts", sub: "Save favorites, get pinged when they drop" },
        { icon: Gift, title: "Invite friends → free Buyer Plus", sub: `Bring ${REFERRAL_GOAL} friends, get a free week`, highlight: true },
      ];
    case "review":
      return [
        { icon: Star, title: "Help other creators", sub: "Your review helps builders pick the right asset" },
        ...DEFAULT_BENEFITS.slice(0, 3),
      ];
    default:
      return DEFAULT_BENEFITS;
  }
}

function benefitsHeadline(intent: PendingIntent, message?: string): string {
  if (intent.kind === "subscribe" && intent.tierKey === "buyer_plus") return "Start your 7-day free trial";
  if (message) return message;
  return "Create your free account";
}

function benefitsSub(intent: PendingIntent, context: SignUpFlowContext | undefined, actionLabel: string, cameFromX: boolean): React.ReactNode {
  const prefix = cameFromX ? "Found us on X? Good eye. " : "";
  switch (intent.kind) {
    case "download":
    case "buy":
    case "cart":
    case "wishlist":
      return (
        <>{prefix}Your {actionLabel} of <strong className="text-foreground">{context?.title ?? "this asset"}</strong> starts the second you verify.</>
      );
    case "checkout":
      return <>{prefix}Your cart is saved — you&rsquo;ll be checking out in seconds.</>;
    case "subscribe":
      return intent.tierKey === "buyer_plus"
        ? <>{prefix}Create your free account and your Buyer Plus trial checkout opens automatically. No charge for 7 days.</>
        : <>{prefix}Create your free account and your subscription checkout opens automatically.</>;
    case "follow":
      return context?.title
        ? <>{prefix}Follow <strong className="text-foreground">{context.title}</strong> to catch every new drop.</>
        : <>{prefix}Follow your favorite creators to catch every new drop.</>;
    case "review":
      return <>{prefix}Sign up free and your review of <strong className="text-foreground">{context?.title ?? "this asset"}</strong> goes live in seconds.</>;
    default:
      return <>{prefix}Sign up free in seconds — everything picks up right where you left off.</>;
  }
}

/**
 * Full-screen, app-style signup flow shown when a logged-out user tries to DO
 * something (download / buy / check out / subscribe / follow…). Instead of a
 * small modal they ignore, it takes over the screen and walks them through it:
 * intent-aware benefits → create account → check your inbox. The intended
 * action is persisted via the pending-intent module and auto-resumes the
 * moment they verify (PendingIntentResumer / the asset page's resume effect).
 */
export function SignUpFlowDialog({
  open, onOpenChange, intent, context, message, onSuccess,
  surface = "express_dialog",
}: SignUpFlowDialogProps) {
  const [step, setStep] = useState<Step>("benefits");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const { isAvailable: usernameAvailable, isChecking: usernameChecking } = useUsernameCheck(username);

  const actionLabel = actionLabelFor(intent);
  const trigger = intentToTrigger(intent);
  const assetId = intentAssetId(intent);
  const cameFromX = getFirstTouchAttribution()?.utm.utm_source === "x";

  // Reset to the first step + fire the funnel-open event each time the flow opens.
  useEffect(() => {
    if (!open) return;
    setStep("benefits");
    setPendingEmail(null);
    setError(null);
    trackExpressSignupOpened({
      asset_id: assetId,
      action_type: trigger,
      is_free: context?.isFree,
      seller_id: context?.sellerId,
    });
  }, [open]);

  // Auth state change covers email signup completing (and OAuth return).
  useEffect(() => {
    if (!open) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        onOpenChange(false);
        onSuccess?.();
      }
    });
    return () => subscription.unsubscribe();
  }, [open, onSuccess, onOpenChange]);

  const persistIntent = () => {
    savePendingIntent(intent);
    recordSignupSurface({ surface, trigger, asset_id: assetId });
  };

  const handleGoogleSignIn = async () => {
    if (!termsAccepted) {
      setError("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setGoogleLoading(true);
    setError(null);

    persistIntent();
    // OAuth carries no metadata — stash consent so it's written once signed in.
    stashPendingConsent({ terms: termsAccepted, marketing: marketingOptIn });
    trackAuthSignupStarted("google");

    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: appendAttributionToUrl(window.location.origin + window.location.pathname) },
    });

    if (authError) {
      setError(authError.message);
      setGoogleLoading(false);
      clearPendingIntent();
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const policyError = passwordPolicyMessage(password);
    if (policyError) {
      setError(policyError);
      setLoading(false);
      return;
    }

    const trimmedUsername = username.toLowerCase().trim();
    if (trimmedUsername.length < 3) {
      setError("Username must be at least 3 characters");
      setLoading(false);
      return;
    }

    if (usernameAvailable === false) {
      setError("Username is taken");
      setLoading(false);
      return;
    }

    if (isTurnstileEnabled() && !captchaToken) {
      setError("Please complete the verification challenge.");
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setError("Please accept the Terms of Service and Privacy Policy to continue.");
      setLoading(false);
      return;
    }

    trackAuthSignupStarted("email");
    try {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: trimmedUsername,
            display_name: trimmedUsername,
            // Consent flags read by the handle_new_user trigger.
            ...(termsAccepted ? { terms_accepted: "true" } : {}),
            marketing_opt_in: marketingOptIn ? "true" : "false",
          },
          emailRedirectTo: window.location.origin + window.location.pathname,
          ...(captchaToken ? { captchaToken } : {}),
        },
      });

      if (signUpError) {
        reportIfRateLimited(signUpError, "auth_signup", email);
        setCaptchaToken(null);
        setTurnstileKey((k) => k + 1);
        setError(
          signUpError.message.includes("already registered")
            ? "Account exists — try signing in instead."
            : toUserFriendlyAuthError(signUpError.message)
        );
        setLoading(false);
        return;
      }

      if (signUpData.user?.id) {
        trackAuthSignupCompleted({ method: "email", userId: signUpData.user.id });
      }

      // Persist the intended action so it auto-resumes when the user returns via
      // the email-confirmation link, and stamp the acquisition surface.
      persistIntent();
      trackSignupVerificationPending({ method: "email", surface });

      setPendingEmail(email);
      setStep("inbox");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const referralTeaser = (
    <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
      <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      <span>
        Invite <strong>{REFERRAL_GOAL} friends</strong> from your dashboard &rarr; a free week of Buyer Plus
        (unlimited downloads).
      </span>
    </div>
  );

  const benefits = benefitsFor(intent);
  const idx = FLOW.indexOf(step);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 bg-background p-0 sm:rounded-none">
        <DialogHeader className="sr-only">
          <DialogTitle>{benefitsHeadline(intent, message)}</DialogTitle>
          <DialogDescription>
            Sign up in a few taps. Your {actionLabel} resumes automatically once you verify.
          </DialogDescription>
        </DialogHeader>

        {/* ── Progress bar ── */}
        <div className="flex shrink-0 items-center gap-3 px-4 py-3.5 sm:px-6">
          {(idx > 0 && step !== "inbox") || step === "signin" ? (
            <button
              type="button"
              onClick={() => setStep(step === "signin" ? "benefits" : FLOW[idx - 1])}
              aria-label="Back"
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : (
            <span className="h-9 w-9" aria-hidden />
          )}
          <div className="flex flex-1 items-center justify-center gap-1.5" aria-hidden>
            {FLOW.map((s, i) => (
              <span
                key={s}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  step === "signin"
                    ? "w-1.5 bg-border"
                    : i === idx ? "w-6 bg-primary" : i < idx ? "w-1.5 bg-primary/60" : "w-1.5 bg-border",
                )}
              />
            ))}
          </div>
          {/* spacer balances the back button; the Dialog's own close ✕ sits top-right */}
          <span className="h-9 w-9" aria-hidden />
        </div>

        {/* ── Step content ── */}
        <div className="flex flex-1 flex-col overflow-y-auto px-5 pb-8 sm:px-6">
          <div key={step} className="mx-auto flex w-full max-w-md flex-1 animate-fade-up flex-col">

            {step === "benefits" && (
              <div className="flex flex-1 flex-col">
                <div className="flex-1">
                  {context?.thumbnail && (
                    <div className="mb-5 flex justify-center">
                      <Image
                        src={context.thumbnail}
                        alt={context.title ?? ""}
                        width={112}
                        height={112}
                        className="h-28 w-28 rounded-2xl border border-border object-cover"
                      />
                    </div>
                  )}
                  <h1 className="text-center font-display text-2xl font-bold tracking-tight">
                    {benefitsHeadline(intent, message)}
                  </h1>
                  <p className="mx-auto mt-2 max-w-xs text-center text-sm text-muted-foreground">
                    {benefitsSub(intent, context, actionLabel, cameFromX)}
                  </p>
                  <ul className="mt-7 space-y-2.5">
                    {benefits.map((b) => (
                      <BenefitRow key={b.title} icon={b.icon} title={b.title} sub={b.sub} highlight={b.highlight} />
                    ))}
                  </ul>
                </div>
                <div className="mt-7 space-y-3">
                  <Button className="h-12 w-full text-base font-semibold" onClick={() => setStep("account")}>
                    Continue <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setStep("signin")}
                      className="font-medium text-primary hover:underline"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </div>
            )}

            {step === "account" && (
              <div className="flex flex-1 flex-col">
                <h1 className="text-center font-display text-2xl font-bold tracking-tight">Create your account</h1>
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  {intent.kind === "subscribe"
                    ? "5 seconds with Google — your free-trial checkout opens right after."
                    : <>5 seconds with Google — your {actionLabel} runs right after.</>}
                </p>

                {error && (
                  <Alert variant="destructive" className="mt-5 py-2">
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="mt-5 space-y-2">
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-muted/30 p-2.5 transition-colors hover:bg-muted/50">
                    <Checkbox
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                      className="mt-0.5"
                      aria-label="Agree to the Terms of Service and Privacy Policy"
                    />
                    <span className="text-xs leading-relaxed text-muted-foreground">
                      I agree to the{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms</a>{" "}
                      and{" "}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>.
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-muted/30 p-2.5 transition-colors hover:bg-muted/50">
                    <Checkbox
                      checked={marketingOptIn}
                      onCheckedChange={(checked) => setMarketingOptIn(checked === true)}
                      className="mt-0.5"
                      aria-label="Receive product updates and promotions by email"
                    />
                    <span className="text-xs leading-relaxed text-muted-foreground">
                      Email me product updates &amp; promotions. Optional — unsubscribe anytime.
                    </span>
                  </label>
                </div>

                <Button
                  type="button"
                  className="mt-4 h-12 w-full border border-border bg-background text-sm font-medium text-foreground hover:bg-secondary"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || loading || !termsAccepted}
                >
                  {googleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <GoogleLogo />
                      Continue with Google
                    </>
                  )}
                </Button>

                <FormAnticipationBar show={googleLoading && !loading} label="Connecting with Google" />

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-3 text-muted-foreground">or sign up with email</span>
                  </div>
                </div>

                <form onSubmit={handleEmailSignUp} className="space-y-3">
                  <AuthFieldShell variant="modal">
                    <Label htmlFor="express-username" className="text-xs">Username</Label>
                    <div className="relative mt-1">
                      <Input
                        id="express-username"
                        placeholder="coolcreator"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="h-10 bg-secondary border-0 pr-8 text-sm"
                        required
                        minLength={3}
                        maxLength={30}
                        autoComplete="username"
                      />
                      {username.length >= 3 && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2">
                          {usernameChecking ? (
                            <Skeleton className="h-3.5 w-3.5 rounded-full" aria-hidden />
                          ) : usernameAvailable ? (
                            <Check className="h-3.5 w-3.5 text-success" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-destructive" />
                          )}
                        </span>
                      )}
                    </div>
                  </AuthFieldShell>

                  <AuthFieldShell variant="modal">
                    <Label htmlFor="express-email" className="text-xs">Email</Label>
                    <Input
                      id="express-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 h-10 bg-secondary border-0 text-sm"
                      required
                      autoComplete="email"
                    />
                  </AuthFieldShell>

                  <AuthFieldShell variant="modal">
                    <Label htmlFor="express-password" className="text-xs">Password</Label>
                    <PasswordInput
                      id="express-password"
                      placeholder={`${AUTH_PASSWORD_MIN_LENGTH}+ characters`}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="mt-1 h-10 text-sm"
                      required
                      minLength={AUTH_PASSWORD_MIN_LENGTH}
                      showIcon={false}
                      autoComplete="new-password"
                    />
                    {password.length > 0 && <PasswordStrength password={password} />}
                  </AuthFieldShell>

                  <TurnstileWidget key={turnstileKey} onToken={setCaptchaToken} className="flex justify-center" />

                  <FormAnticipationBar show={loading || googleLoading} label="Please wait" />

                  <Button
                    type="submit"
                    className="h-12 w-full text-base font-semibold"
                    disabled={loading || googleLoading || !termsAccepted || (username.length >= 3 && usernameAvailable === false) || (password.length > 0 && !evaluatePasswordPolicy(password).ok) || (isTurnstileEnabled() && !captchaToken)}
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                    {submitLabelFor(intent)}
                  </Button>
                </form>

              </div>
            )}

            {step === "signin" && (
              <div className="flex flex-1 flex-col">
                <h1 className="text-center font-display text-2xl font-bold tracking-tight">Welcome back</h1>
                <p className="mt-2 text-center text-sm text-muted-foreground">
                  Sign in and your {actionLabel} picks up right where you left off.
                </p>

                <Button
                  type="button"
                  className="mt-6 h-12 w-full border border-border bg-background text-sm font-medium text-foreground hover:bg-secondary"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                >
                  {googleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <GoogleLogo />
                      Continue with Google
                    </>
                  )}
                </Button>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-3 text-muted-foreground">or sign in with email</span>
                  </div>
                </div>

                <SignInForm
                  idPrefix="flow-signin"
                  onBeforeSubmit={persistIntent}
                  onNavigate={() => onOpenChange(false)}
                />

                <p className="mt-4 text-center text-xs text-muted-foreground">
                  New here?{" "}
                  <button
                    type="button"
                    onClick={() => setStep("account")}
                    className="font-medium text-primary hover:underline"
                  >
                    <UserPlus className="mr-1 inline h-3 w-3" aria-hidden />
                    Create a free account
                  </button>
                </p>
              </div>
            )}

            {step === "inbox" && pendingEmail && (
              <div className="flex flex-1 flex-col justify-center">
                <CheckYourEmailScreen
                  email={pendingEmail}
                  surface={surface}
                  actionLabel={actionLabel}
                  resendSlot={<ResendVerification email={pendingEmail} />}
                  footer={referralTeaser}
                />
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleLogo() {
  return (
    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function BenefitRow({ icon: Icon, title, sub, highlight }: {
  icon: LucideIcon; title: string; sub: string; highlight?: boolean;
}) {
  return (
    <li className={cn("flex items-start gap-3 rounded-xl border p-3", highlight ? "border-primary/30 bg-primary/5" : "border-border/60")}>
      <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", highlight ? "bg-primary/15 text-primary" : "bg-muted text-foreground")}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
    </li>
  );
}

export default SignUpFlowDialog;
